import React, { useEffect, useState } from "react";
import axios from "axios";
import { useRequestContext } from "../../hooks/useRequest";
import { Collapsible, PdfShower } from "../../../components";
import { useHistory } from "react-router-dom";
import { ToggleButton } from "../../../listings/atoms";
import { SectionTrigger } from "../../../orderManagement/molecules/SectionTrigger";
import { pxSellSectionId } from "../../v2/sectionConfig";
import { AgreementFooter } from "./AgreementFooter";
import { useEntitlement } from "../../../contexts";

export function PurchaseOrder({ step, idx, navigateTo }) {
  const history = useHistory();
  const { getContext, onPurchaseOrderSend, toggleStep } = useRequestContext();
  // Step-aware so a PX group's per-sell-request instance raises the PO against
  // ITS trade-in, not against the parent order request.
  const { request } = getContext(idx, step);
  const existUserCompany = request?.client?.company;
  const initFileId = request?.xero_purchase_order_file_id;
  const [fileId, setFileId] = useState(initFileId);
  // The PO is a gated sensitive file, so the backend 403s a token-less
  // GET /api/pdfs/{id} — which is exactly what an <object data=…> issues.
  // Render the temporary signed URL the backend returns instead; keep the
  // numeric fileId for the send-purchase-order call below.
  const [pdfUrl, setPdfUrl] = useState(
    request?.xero_purchase_order_file_url || null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [toggleStatus, setToggleStatus] = useState(request?.toggle_status);

  // A Xero org must be chosen on the PAYMENTS section before we generate the PO,
  // so it can't be created in the wrong organisation.
  const hasXeroOrg = !!request?.xero_connection_id;
  // …unless the store has no Xero on its plan: then the backend renders our
  // own PO PDF and there is no organisation to choose. A PO already issued in
  // Xero stays there.
  const nativePo =
    !useEntitlement("xero_purchase_orders") && !request?.xero_purchase_order_id;
  const canGenerate = hasXeroOrg || nativePo;

  const getPDF = async (toggleStatus) => {
    if (!canGenerate) return; // gated until an org is selected on Payments
    const { data } = await axios.post(
      `/api/requests/${request.id}/pdf-purchase-order`,
      {
        toggle_status: toggleStatus,
        // Generate the PO in the org chosen in the PAYMENTS section. The backend
        // pins it the first time and ignores it once a PO exists.
        xero_connection_id: request.xero_connection_id,
      }
    );

    setFileId(data.xero_purchase_order_file_id);
    setPdfUrl(data.xero_purchase_order_file_url || null);
  };

  const toggleStatusManagement = () => {
    setToggleStatus((prev) => !prev);
  };

  const onOpen = async () => {
    getPDF(request.toggle_status);
  };

  // Suffixed per trade-in inside a PX slot; the static id elsewhere.
  const advanceToPublish = () =>
    navigateTo?.(pxSellSectionId("publish_listing", step?.pxSellIdx));

  const onSave = () => {
    onPurchaseOrderSend();
    advanceToPublish();
  };

  const onSend = async () => {
    setIsLoading(true);
    await axios.post(`/api/requests/${request.id}/send-purchase-order/${fileId}`);
    setIsLoading(false);
    onPurchaseOrderSend();
    advanceToPublish();
  };

  useEffect(() => {
    getPDF(toggleStatus);
  }, [toggleStatus]);

  return (
    <Collapsible
      trigger={<SectionTrigger label={step.label} icon="po" />}
      open={step.open}
      onOpen={() => onOpen()}
      testId="confirm-request"
      handleTriggerClick={() => toggleStep(idx)}
    >
      <div className="order-invoice mt-4">
        {!canGenerate ? (
          <div className="placeholder">
            Select a Xero organisation in the Payments section before generating
            the purchase order.
          </div>
        ) : pdfUrl ? (
          <PdfShower fileId={pdfUrl} />
        ) : (
          <div className="placeholder"> </div>
        )}
        <div className="d-flex justify-content-between">
          <div className="text-left mt-5">
            <ToggleButton
              label="Use Company Name"
              disabled={!existUserCompany}
              checked={toggleStatus}
              onChange={toggleStatusManagement}
            />
          </div>
          <AgreementFooter
            feature="po"
            className="mt-5"
            onSave={onSave}
            onSend={onSend}
            sending={isLoading}
          />
        </div>
      </div>
    </Collapsible>
  );
}
